import { Select } from "@msuite/ui/form";
import { useTranslation } from "@msuite/ui/localization";
import { invalidate, useQuery, useQueryClient } from "@msuite/ui/query";
import { SettingsItem } from "@msuite/ui/settings";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import { guardNull } from "@msuite/utils/misc";
import { useForm } from "react-hook-form";
import { canManageMembers } from "@/components/citadel";
import { client } from "@/hono";
import { useRoles } from "@/hooks/use-roles";

/**
 * The mount is the guard: both queries below are gated `manage Permission` on the server, so
 * the parent renders this only for a caller `canManageRoles` admits. A guard inside, after the
 * hooks, would be too late — the refused reads would already be on the wire, toasting twice at
 * a reader who was never offered the control.
 */
export function PersonioSyncRole() {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();

	/** Form */
	const methods = useForm<{ syncRoleId: string | null }>({
		defaultValues: { syncRoleId: null },
	});

	/** Query */
	// Kept, not only pushed into the form: it is the last value the server confirmed, and
	// therefore what the control has to fall back to when a write is refused.
	const { data: storedSyncRoleId } = useQuery({
		queryKey: ["personio-sync-role"],
		queryFn: async () => {
			const response = await client.integrations.personio["sync-role"].$get();
			const data = await responseHandler(response);
			return data.syncRoleId;
		},
		onSuccess: (syncRoleId) => methods.reset({ syncRoleId }),
	});
	const { roles } = useRoles();

	/** Functions */
	async function handleOnChangeSyncRole(syncRoleId: string | null) {
		try {
			const response = await client.integrations.personio["sync-role"].$patch({
				json: { syncRoleId },
			});
			// One message per outcome, raised here — the same shape every other role write uses.
			await responseHandler(response, false);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : t("general.something_went_wrong"));
			// The select writes optimistically — `field.onChange` runs before this callback — so a
			// refused write leaves the control showing a role that was never saved. The refetch
			// below cannot undo that on its own: an unchanged stored value comes back
			// referentially equal, so the query's `onSuccess` effect does not re-run and the form
			// is never reset. The snap-back is done here, from the last confirmed value.
			methods.reset({ syncRoleId: guardNull(storedSyncRoleId) });
		} finally {
			// On both paths, because the refused write is exactly the case where the cached value
			// and the control have drifted apart.
			await qc.invalidateQueries({ predicate: invalidate(["personio-sync-role"]) });
		}
	}

	/** Render */
	// Clearing the select leaves the sync unconfigured, which is a refusal rather than a
	// default: the next run stops instead of guessing a role for the people it creates.
	return (
		<SettingsItem
			title={t("integrations.personio_sync_role")}
			description={t("integrations.personio_sync_role_description")}
		>
			<Select
				control={methods.control}
				name="syncRoleId"
				label={undefined}
				isRequired={false}
				onChange={handleOnChangeSyncRole}
				onRemove={() => handleOnChangeSyncRole(null)}
				className="py-1 w-[200px]"
				data-cy="personio-sync-role-select"
				// The member system role is left out: every synced person receives it at joining
				// regardless, so this setting only names what they carry on top — the same rule
				// the add-user form's picker follows.
				options={roles
					.filter((role) => canManageMembers(role))
					.map((role) => ({
						value: role.id,
						label: role.name,
						item: null,
					}))}
			/>
		</SettingsItem>
	);
}
